/** Fidelização form tab (Em Tratamento → Fidelizados). */

import type {
  AssinadoDadosData,
  EnvioPropostaData,
  FidelizadosDadosData,
  FidelizadosServicoItem,
  TentativasContactoData,
  WorkspaceMotivoData,
} from '../api/cccApi';
import { isAssinadoSubstage } from './assinadoDados';
import { isEnvioPropostaSubstage } from './envioProposta';
import { normalizeWorkspaceSubstage } from './recuperacaoWorkspace';
import {
  isNaoAtendidoSubstage,
  isTentativasContactoIncomplete,
} from './tentativasContacto';
import { getMotivoVariantForSubstage, isWorkspaceMotivoIncomplete, MOTIVO_WARN_SUBSTAGES } from './workspaceMotivos';
import { readWorkspaceMotivoKeys } from '../utils/motivoKeys';

export const FIDELIZADOS_SUBSTAGE = 'em-tratamento-fidelizados';

export const FIDELIZADOS_TAB_SUBSTAGES = new Set([FIDELIZADOS_SUBSTAGE]);

export const FIDELIZADOS_WARNING_TEXT =
  "Preencha o campo obrigatório 'Fornecedor Atual'";

export const WORKSPACE_FORM_WARN_SUBSTAGES = new Set([
  ...MOTIVO_WARN_SUBSTAGES,
  ...FIDELIZADOS_TAB_SUBSTAGES,
]);

export function isFidelizadosSubstage(substage: string): boolean {
  return FIDELIZADOS_TAB_SUBSTAGES.has(normalizeWorkspaceSubstage(substage));
}

export function servicoRowHasData(row: FidelizadosServicoItem): boolean {
  const nome = (row.nome || '').trim();
  const qty = row.quantidade;
  const hasQty = qty !== null && qty !== undefined && qty !== '' && !Number.isNaN(Number(qty));
  const valor = row.valor_fatura;
  const hasValor =
    valor !== null && valor !== undefined && valor !== '' && !Number.isNaN(Number(valor));
  return Boolean(nome || hasQty || hasValor);
}

export function isFidelizadosDadosIncomplete(dados?: FidelizadosDadosData | null): boolean {
  if (!dados) return true;
  const fornecedor = (dados.fornecedor_atual || '').trim();
  return !fornecedor;
}

function isEnvioPropostaIncomplete(dados?: EnvioPropostaData | null): boolean {
  if (!dados) return true;
  if (dados.proposta_enviada === true) {
    const dataEnvio = (dados.data_envio || '').trim();
    const enviadoPara = (dados.enviado_para || '').trim();
    return !dataEnvio || !enviadoPara;
  }
  if (dados.proposta_enviada === false) {
    const motivo = (dados.motivo_nao_enviada || '').trim();
    if (!motivo) return true;
    if (motivo === 'outro' && !(dados.motivo_nao_enviada_outro || '').trim()) return true;
    if (motivo === 'fidelizado') {
      return !(dados.fornecedor_atual || '').trim() || !(dados.data_fim_contrato || '').trim();
    }
    return false;
  }
  return true;
}

function isAssinadoDadosIncomplete(dados?: AssinadoDadosData | null): boolean {
  if (!dados) return true;
  if (dados.assinado === true) {
    return !(dados.data_assinatura || '').trim();
  }
  if (dados.assinado === false) {
    const motivo = (dados.motivo_nao_assinado || '').trim();
    if (!motivo) return true;
    if (motivo === 'outro') {
      const feedback = (dados.motivo_nao_assinado_outro || '').trim();
      if (!feedback) return true;
    }
    if (motivo === 'fidelizado') {
      const fornecedor = (dados.fornecedor_atual || '').trim();
      const dataFim = (dados.data_fim_contrato || '').trim();
      return !fornecedor || !dataFim;
    }
    return false;
  }
  return true;
}

export function isMarcacaoWorkspaceFormIncomplete(
  substage: string,
  claim?: {
    sem_interesse_motivo?: WorkspaceMotivoData | null;
    pendente_motivo?: WorkspaceMotivoData | null;
    fidelizados_dados?: FidelizadosDadosData | null;
    envio_proposta?: EnvioPropostaData | null;
    assinado_dados?: AssinadoDadosData | null;
    tentativas_contacto?: TentativasContactoData | null;
  } | null
): boolean {
  const normalized = normalizeWorkspaceSubstage(substage);
  const motivoVariant = getMotivoVariantForSubstage(normalized);
  if (motivoVariant === 'pendente') {
    const d = claim?.pendente_motivo;
    const hasMotivo = readWorkspaceMotivoKeys(d).length > 0;
    const hasFeedback = Boolean(d?.feedback?.trim());
    return !hasMotivo || !hasFeedback;
  }
  if (motivoVariant === 'sem_interesse') {
    const d = claim?.sem_interesse_motivo;
    const hasMotivo = readWorkspaceMotivoKeys(d).length > 0;
    const hasFeedback = Boolean(d?.feedback?.trim());
    return !hasMotivo || !hasFeedback;
  }
  if (FIDELIZADOS_TAB_SUBSTAGES.has(normalized)) {
    return isFidelizadosDadosIncomplete(claim?.fidelizados_dados);
  }
  if (isEnvioPropostaSubstage(normalized)) {
    return isEnvioPropostaIncomplete(claim?.envio_proposta);
  }
  if (isAssinadoSubstage(normalized)) {
    return isAssinadoDadosIncomplete(claim?.assinado_dados);
  }
  if (isNaoAtendidoSubstage(normalized)) {
    return isTentativasContactoIncomplete(claim?.tentativas_contacto);
  }
  return false;
}

export const SPECIAL_TAB_SUBSTAGES_REQUIRING_COMPLETION = new Set([
  ...MOTIVO_WARN_SUBSTAGES,
  ...FIDELIZADOS_TAB_SUBSTAGES,
  'pre-fecho-proposta-enviada',
  'pre-fecho-assinado',
  'por-contactar-nao-atendido',
  'agendamentos-remarcacao',
]);

export const emptyServicoRow = (): FidelizadosServicoItem => ({
  nome: '',
  quantidade: null,
  valor_fatura: null,
});
